/**
 * EGJam - the band: a drummer and a bass player that follow the genre.
 *
 * WHY A BAND AND NOT A METRONOME
 * ------------------------------
 * "Play a solo over a backing track with the pentatonic lit up on the neck"
 * is the practice loop rock guitarists actually use (one backing-track
 * channel alone has 185 M views). A browser guitar without it leaves the
 * player with nothing to play against, and a metronome click is not a groove.
 *
 * The drums are the CC0 VCSL kit that /virtual-drums already ships, loaded
 * from the same URLs so the service worker's cache is shared. The bass is the
 * SAME waveguide as the guitar (eg-core.js, a 34" four-string model) through a
 * small bass amp, played by a line generator that follows the chords.
 *
 * Everything is scheduled on the AudioContext clock with a look-ahead, and
 * the same pattern code renders into an OfflineAudioContext for the export -
 * the backing in the file is the backing you played to.
 */
(function (root, factory) {
    var api = factory();
    if (typeof globalThis !== 'undefined') globalThis.EGJam = api;
    if (typeof window !== 'undefined') window.EGJam = api;
}(this, function () {
    'use strict';

    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

    /* Seeded, so an export renders the same humanised drummer as the preview. */
    function rng(seed) {
        var a = seed | 0;
        return function () {
            a = (a + 0x6D2B79F5) | 0;
            var t = Math.imul(a ^ (a >>> 15), 1 | a);
            t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }

    var PIECE_GAIN = { kick: 1.0, snare: 0.85, hatClosed: 0.38, hatOpen: 0.42, ride: 0.4, crash: 0.55, tom1: 0.8, tom2: 0.8, tom3: 0.85 };
    var PIECE_PAN = { kick: 0, snare: 0.03, hatClosed: -0.3, hatOpen: -0.3, ride: 0.35, crash: 0.25, tom1: -0.12, tom2: 0.12, tom3: 0.28 };
    var LETTER = { k: 'kick', s: 'snare', h: 'hatClosed', o: 'hatOpen', r: 'ride', c: 'crash', t1: 'tom1', t2: 'tom2', t3: 'tom3' };

    /* Bass line styles per groove. */
    var BASS_STYLE = {
        rock8: 'eighths', rock8b: 'eighths', punk: 'eighths', motorik: 'eighths', surf: 'eighths',
        gallop: 'gallop', thrash: 'eighths', djent: 'follow', half: 'half', doom: 'half', ballad: 'half',
        shuffle: 'boogie', swing: 'walk', funk: 'funk'
    };

    // ───────────────────────────── the drummer ────────────────────────────
    function Drums(ctx, dest, base, manifestUrl) {
        this.ctx = ctx;
        this.dest = dest;
        this.base = base;
        this.manifestUrl = manifestUrl;
        this.buffers = null;          // piece -> [[layer rr buffers]]
        this.loading = null;
        this.pans = {};
        this.fmt = (function () {
            try { var a = document.createElement('audio'); if (a.canPlayType && a.canPlayType('audio/ogg; codecs="opus"')) return 'ogg'; } catch (e) {}
            return 'mp3';
        }());
    }
    Drums.prototype.load = function () {
        if (this.buffers) return Promise.resolve(this.buffers);
        if (this.loading) return this.loading;
        var self = this, ctx = this.ctx;
        function get(url) {
            return fetch(url, { credentials: 'same-origin' }).then(function (r) {
                if (!r.ok) throw new Error('HTTP ' + r.status);
                return r.arrayBuffer();
            });
        }
        function decode(ab) {
            return new Promise(function (res, rej) {
                var p = ctx.decodeAudioData(ab.slice(0), res, rej);
                if (p && p.then) p.then(res, rej);
            });
        }
        this.loading = get(this.manifestUrl).then(function (ab) {
            var man = JSON.parse(new TextDecoder().decode(ab));
            var kit = man.kits.acoustic.pieces;
            var jobs = [], out = {};
            Object.keys(PIECE_GAIN).forEach(function (piece) {
                var def = kit[piece];
                if (!def) return;
                out[piece] = [];
                def.layers.forEach(function (layer, li) {
                    out[piece][li] = [];
                    layer.forEach(function (name, ri) {
                        /* Opus is asked for when the browser SAYS it can play it, and
                           canPlayType answers "maybe" - a browser that then fails to
                           decode it (older Safari) would give a band without drums
                           and no error anywhere. Each file falls back to MP3. */
                        var url = self.base + '/acoustic/' + name + '.';
                        jobs.push(get(url + self.fmt).then(decode).catch(function () {
                            if (self.fmt === 'mp3') throw new Error('mp3');
                            self.fellBack = (self.fellBack || 0) + 1;
                            return get(url + 'mp3').then(decode);
                        }).then(function (buf) {
                            out[piece][li][ri] = buf;
                        }).catch(function () { self.failed = (self.failed || 0) + 1; }));
                    });
                });
            });
            return Promise.all(jobs).then(function () {
                self.buffers = out;
                if ((self.failed || self.fellBack) && self.onReport) self.onReport({ kind: 'drums', failed: self.failed || 0, fell_back: self.fellBack || 0, fmt: self.fmt });
                return out;
            });
        });
        return this.loading;
    };
    /* Also used by the offline export: decoded buffers are context-agnostic
       (an AudioBuffer plays in any context of the same rate). */
    Drums.prototype.hit = function (ctx, dest, piece, when, vel, r) {
        var set = this.buffers && this.buffers[piece];
        if (!set || !set.length) return;
        var li = clamp(Math.round(vel * (set.length - 1) + (r() - 0.5) * 0.6), 0, set.length - 1);
        var layer = set[li] && set[li].length ? set[li] : set[set.length - 1];
        var buf = layer[Math.floor(r() * layer.length) % layer.length];
        if (!buf) return;
        var src = ctx.createBufferSource();
        src.buffer = buf;
        var g = ctx.createGain();
        g.gain.value = (PIECE_GAIN[piece] || 0.6) * (0.55 + 0.45 * vel);
        src.connect(g);
        var pan = PIECE_PAN[piece] || 0;
        if (pan && ctx.createStereoPanner) {
            var p = ctx.createStereoPanner(); p.pan.value = pan; g.connect(p); p.connect(dest);
        } else g.connect(dest);
        src.start(Math.max(when, ctx.currentTime));
        if (piece === 'hatOpen') this._openHat = { src: src, g: g };
        if (piece === 'hatClosed' && this._openHat && ctx === this.ctx) {
            // choke: a closing hat cuts the open one
            try { this._openHat.g.gain.setTargetAtTime(0, when, 0.01); } catch (e) {}
            this._openHat = null;
        }
    };

    // ─────────────────────────── the band leader ──────────────────────────
    /*
     * A jam state: { groove, bpm, key, scale, prog: [[sym, bars]...], swing }
     * The song form is the progression looped. Bar 1 of every cycle gets a
     * crash, the last beat of every 4th bar a small fill.
     */
    function Band(opts) {
        this.P = opts.presets;          // EGPresets
        this.state = null;
        this.seed = opts.seed || 4242;
    }
    Band.prototype.set = function (st) { this.state = st; };
    Band.prototype.form = function () {
        var st = this.state, bars = [];
        for (var i = 0; i < st.prog.length; i++) for (var b = 0; b < st.prog[i][1]; b++) bars.push(st.prog[i][0]);
        return bars;
    };
    /* All events of bar `n` (0-based, absolute), times in beats from bar start. */
    Band.prototype.bar = function (n) {
        var st = this.state, P = this.P;
        var gr = P.GROOVES[st.groove] || P.GROOVES.rock8;
        var form = this.form();
        var sym = form[n % form.length];
        var steps = gr.steps;
        var per = 4 / steps;                          // beats per step
        var r = rng(this.seed + n * 7919);
        var drums = [];
        function lane(key, piece) {
            var pat = gr[key];
            if (!pat) return;
            for (var i = 0; i < steps; i++) {
                var ch = pat.charAt(i);
                if (ch === '.' || !ch) continue;
                var ghost = ch === 'g';
                var accent = (i % (steps / 4)) === 0;
                var vel = ghost ? 0.22 : (accent ? 0.9 : 0.72);
                drums.push({ beat: i * per, piece: piece, vel: clamp(vel + (r() - 0.5) * 0.14, 0.1, 1) });
            }
        }
        lane('k', 'kick'); lane('s', 'snare'); lane('h', 'hatClosed'); lane('r', 'ride');
        lane('t3', 'tom3'); lane('c', 'crash');
        if (n % form.length === 0) drums.push({ beat: 0, piece: 'crash', vel: 0.85 });
        if (n % 4 === 3) {
            // fill on beat 4: snare + toms, drop the hats under it
            drums = drums.filter(function (d) { return d.beat < 3 || d.piece === 'kick'; });
            var fill = steps === 12 ? [3, 3.333, 3.667] : [3, 3.25, 3.5, 3.75];
            var pieces = ['snare', 'tom1', 'tom2', 'tom3'];
            fill.forEach(function (b, i) { drums.push({ beat: b, piece: pieces[i % 4], vel: 0.7 + 0.1 * i }); });
        }
        // bass
        var c = P.parseChord(sym);
        var rootPc = (P.NOTE[st.key] + c.deg) % 12;
        var style = BASS_STYLE[st.groove] || 'eighths';
        var bass = bassLine(style, rootPc, c, steps, per, r);
        return { chord: P.chordName(st.key, sym, st.scale), sym: sym, drums: drums, bass: bass, rootPc: rootPc };
    };

    /* Bass notes as {beat, len, midi, vel, pm}. E1 = MIDI 28 is the lowest. */
    function bassRoot(pc) { return 28 + ((pc - 4 + 12) % 12); }
    function bassLine(style, pc, c, steps, per, r) {
        var root = bassRoot(pc);
        if (root < 28) root += 12;
        var fifth = root + 7, oct = root + 12;
        var third = root + (c.minor ? 3 : 4), sixth = root + 9, b7 = root + 10;
        var out = [];
        function n(beat, len, midi, vel, pm) { out.push({ beat: beat, len: len, midi: midi, vel: vel, pm: !!pm }); }
        if (style === 'eighths') {
            for (var i = 0; i < 8; i++) n(i * 0.5, 0.45, i === 7 && r() < 0.35 ? fifth : root, i % 2 ? 0.62 : 0.8, true);
        } else if (style === 'gallop') {
            for (var g = 0; g < 4; g++) { n(g, 0.45, root, 0.82, true); n(g + 0.5, 0.22, root, 0.6, true); n(g + 0.75, 0.22, root, 0.6, true); }
        } else if (style === 'half') {
            n(0, 1.9, root, 0.85); n(2, 1.4, r() < 0.5 ? root : fifth, 0.75); n(3.5, 0.45, oct, 0.55);
        } else if (style === 'follow') {
            [0, 0.75, 1.5, 2, 2.75, 3.5].forEach(function (b) { n(b, 0.2, root, 0.85, true); });
        } else if (style === 'boogie') {
            var seq = [root, third, fifth, sixth, b7, sixth, fifth, third];
            for (var q = 0; q < 4; q++) { n(q, 0.62, seq[q * 2], 0.82); n(q + 0.667, 0.3, seq[q * 2 + 1], 0.6); }
        } else if (style === 'walk') {
            var w = [root, third, fifth, sixth];
            for (var q2 = 0; q2 < 4; q2++) n(q2, 0.62, w[q2], 0.8);
        } else if (style === 'funk') {
            [[0, 0.2, root], [0.75, 0.15, root], [1, 0.2, oct], [1.5, 0.2, root], [2.25, 0.15, fifth], [2.5, 0.2, b7], [3, 0.2, oct], [3.5, 0.2, root]]
                .forEach(function (x) { n(x[0], x[1], x[2], 0.8); });
        }
        return out;
    }

    /* Beat time with swing for the triplet grooves. */
    function beatTime(beat, secPerBeat) { return beat * secPerBeat; }

    // ─────────────────────────────── live jam ─────────────────────────────
    function Jam(opts) {
        this.ctx = opts.ctx;
        this.out = opts.ctx.createGain();
        this.drumBus = opts.ctx.createGain();
        this.drumBus.connect(this.out);
        this.drums = new Drums(opts.ctx, this.drumBus, opts.drumsBase, opts.manifestUrl);
        this.band = new Band({ presets: opts.presets, seed: opts.seed });
        this.bassPlay = opts.bassPlay;     // function(eventsWithTimes)
        this.onBar = opts.onBar || function () {};
        this.onBeat = opts.onBeat || function () {};
        this.timer = null;
        this.playing = false;
        this.nextBar = 0; this.nextBarTime = 0; this.startTime = 0;
        this.metronome = false;
        this.countIn = 0;
        this.drumsOn = true; this.bassOn = true;
    }
    Jam.prototype.setState = function (st) {
        this.band.set(st);
        this.secPerBeat = 60 / st.bpm;
    };
    Jam.prototype.start = function (st, when) {
        var self = this;
        this.setState(st);
        this.playing = true;
        var ctx = this.ctx;
        return this.drums.load().catch(function () {}).then(function () {
            if (!self.playing) return;
            self.startTime = Math.max(ctx.currentTime + 0.08, when || 0);
            self.nextBar = 0;
            self.nextBarTime = self.startTime;
            clearInterval(self.timer);
            self.timer = setInterval(function () { self._tick(); }, 25);
            self._tick();
        });
    };
    Jam.prototype.stop = function () {
        this.playing = false;
        clearInterval(this.timer); this.timer = null;
        if (this._uiTimers) { this._uiTimers.forEach(clearTimeout); }
        this._uiTimers = [];
    };
    /* Change tempo/key/groove at the next bar line without stopping. */
    /* A new tempo takes over from the next bar line; the clock that says where
       the band is (the riff's start on a bar, the bar/beat display) is moved so
       that it agrees - it used to count every bar since the start at the new
       tempo, and a riff after a BPM change began off the bar line. */
    Jam.prototype.update = function (st) {
        var old = this.secPerBeat;
        this.setState(st);
        if (this.playing && old && this.secPerBeat !== old && this.nextBarTime) {
            this.startTime = this.nextBarTime - this.nextBar * 4 * this.secPerBeat;
        }
    };
    Jam.prototype._tick = function () {
        if (!this.playing) return;
        /* A player often switches to a tab with the song's tabs and plays on:
           a hidden page may get its timers throttled to about once a second,
           so the band is scheduled further ahead there. A bar the clock has
           already passed (a stalled main thread) is skipped, not crammed into
           one burst of hits. */
        var ctx = this.ctx, now = ctx.currentTime;
        var horizon = now + (typeof document !== 'undefined' && document.hidden ? 1.5 : 0.2);
        var barDur = 4 * this.secPerBeat;
        while (this.nextBarTime + barDur < now) { this.nextBarTime += barDur; this.nextBar++; }
        while (this.nextBarTime < horizon) {
            this._scheduleBar(this.nextBar, this.nextBarTime);
            this.nextBarTime += 4 * this.secPerBeat;
            this.nextBar++;
        }
    };
    /* Every hit the band schedules is also told to `onHit` (a take records what
       the band PLAYED - tempo changes, drums switched off and fills included)
       and kept for a moment in `recent`: the band schedules up to 1.5 s ahead,
       and a take that starts inside that window needs the hits already queued. */
    Jam.prototype._heard = function (hit) {
        var now = this.ctx.currentTime, rec = this.recent || (this.recent = []);
        rec.push(hit);
        while (rec.length && rec[0].t < now - 0.5) rec.shift();
        if (this.onHit) this.onHit(hit);
    };
    Jam.prototype._scheduleBar = function (n, t0) {
        var ctx = this.ctx, spb = this.secPerBeat, self = this;
        var bar = this.band.bar(n);
        var r = rng(9000 + n);
        if (this.drumsOn) {
            for (var i = 0; i < bar.drums.length; i++) {
                var d = bar.drums[i];
                var jitter = d.piece === 'kick' || d.piece === 'snare' ? (r() - 0.5) * 0.006 : (r() - 0.5) * 0.01;
                var when = t0 + beatTime(d.beat, spb) + jitter;
                this.drums.hit(ctx, this.drumBus, d.piece, when, d.vel, r);
                this._heard({ t: when, piece: d.piece, vel: d.vel });
            }
        }
        if (this.metronome) {
            for (var b = 0; b < 4; b++) this._click(t0 + b * spb, b === 0);
        }
        if (this.bassOn && this.bassPlay) {
            var evs = [];
            bar.bass.forEach(function (x) {
                var t = t0 + beatTime(x.beat, spb);
                evs.push({ t: t, midi: x.midi, vel: x.vel, pm: x.pm, len: x.len * spb });
            });
            this.bassPlay(evs);
        }
        // UI: bar and beats on the wall clock, aligned to the audio clock
        var lead = Math.max(0, (t0 - ctx.currentTime) * 1000);
        this._uiTimers = this._uiTimers || [];
        this._uiTimers.push(setTimeout(function () { self.onBar(n, bar); }, lead));
        for (var bt = 0; bt < 4; bt++) {
            (function (bt) {
                self._uiTimers.push(setTimeout(function () { self.onBeat(n, bt); }, lead + bt * spb * 1000));
            }(bt));
        }
        if (this._uiTimers.length > 64) this._uiTimers.splice(0, this._uiTimers.length - 64);
    };
    Jam.prototype._click = function (when, accent) {
        var ctx = this.ctx;
        var o = ctx.createOscillator(), g = ctx.createGain();
        o.frequency.value = accent ? 1760 : 1320;
        g.gain.setValueAtTime(0.0001, when);
        g.gain.exponentialRampToValueAtTime(accent ? 0.35 : 0.22, when + 0.002);
        g.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
        o.connect(g); g.connect(this.out);
        o.start(when); o.stop(when + 0.06);
    };
    /* Where are we? For the UI and for recording the jam into a take. */
    Jam.prototype.position = function () {
        if (!this.playing) return null;
        var t = this.ctx.currentTime - this.startTime;
        if (t < 0) return { bar: -1, beat: 0 };
        var beats = t / this.secPerBeat;
        return { bar: Math.floor(beats / 4), beat: beats % 4, t0: this.startTime };
    };

    /*
     * Offline: render `bars` bars starting at `t0` into (ctx, dest), with the
     * bass notes handed back as core events for the caller's bass renderer.
     */
    function renderOffline(opts) {
        var band = new Band({ presets: opts.presets, seed: opts.seed || 4242 });
        band.set(opts.state);
        var spb = 60 / opts.state.bpm;
        var bassEvents = [];
        for (var n = 0; n < opts.bars; n++) {
            var t0 = opts.t0 + n * 4 * spb;
            var bar = band.bar(n);
            var r = rng(9000 + n);
            if (opts.drums && opts.drums.buffers) {
                for (var i = 0; i < bar.drums.length; i++) {
                    var d = bar.drums[i];
                    var jitter = d.piece === 'kick' || d.piece === 'snare' ? (r() - 0.5) * 0.006 : (r() - 0.5) * 0.01;
                    opts.drums.hit(opts.ctx, opts.dest, d.piece, t0 + d.beat * spb + jitter, d.vel, r);
                }
            }
            bar.bass.forEach(function (x) { bassEvents.push({ t: t0 + x.beat * spb, midi: x.midi, vel: x.vel, pm: x.pm, len: x.len * spb }); });
        }
        return bassEvents;
    }

    /* MIDI note -> (string, fret) on a four-string bass, lowest position. */
    function bassPosition(midi) {
        var open = [43, 38, 33, 28];
        for (var s = 3; s >= 0; s--) {
            var f = midi - open[s];
            if (f >= 0 && f <= 7) return { s: s, fret: f };
        }
        for (var s2 = 0; s2 < 4; s2++) { var f2 = midi - open[s2]; if (f2 >= 0 && f2 <= 20) return { s: s2, fret: f2 }; }
        return { s: 3, fret: 0 };
    }
    /* Bass note events -> core events (on + off). */
    function bassCoreEvents(list) {
        var out = [];
        for (var i = 0; i < list.length; i++) {
            var x = list[i], pos = bassPosition(x.midi);
            out.push({ t: x.t, type: 'on', s: pos.s, fret: pos.fret, vel: x.vel, o: { pm: x.pm ? 0.35 : 0 } });
            out.push({ t: x.t + Math.max(0.05, x.len), type: 'off', s: pos.s, rel: 0.05 });
        }
        return out;
    }

    return {
        VERSION: 1,
        Jam: Jam,
        Drums: Drums,
        Band: Band,
        renderOffline: renderOffline,
        bassCoreEvents: bassCoreEvents,
        bassPosition: bassPosition,
        BASS_STYLE: BASS_STYLE
    };
}));
